import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import {
  MapPin,
  Navigation,
  Search,
  X,
  AlertCircle,
  CheckCircle2,
  Loader2,
  SlidersHorizontal,
  Move,
} from 'lucide-react';
import {
  ALLOWED_NEARBY_RADII_KM,
  NearbyRadiusKm,
  isGoogleMapsConfigured,
} from '../../config/googleMaps';
import { isValidCoordinatePair, isWithinNigeriaBounds } from '../../utils/locationCoordinates';

export interface CustomerSelectedLocation {
  lat: number;
  lng: number;
  label: string;
  source: 'browser_gps' | 'google_places' | 'map_pin' | 'lga_selection';
  accuracyMeters?: number;
  state?: string;
  lga?: string;
}

interface CustomerNearbyLocationBarProps {
  customerLocation: CustomerSelectedLocation | null;
  onLocationChange: (loc: CustomerSelectedLocation | null) => void;
  radiusKm: NearbyRadiusKm;
  onRadiusChange: (radius: NearbyRadiusKm) => void;
  onOpenMapPinAdjust?: () => void;
  isMapVisible?: boolean;
}

interface PlaceSuggestionItem {
  id: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
  rawPlacePrediction?: any;
  fallbackCoords?: { lat: number; lng: number; state?: string; lga?: string };
}

/**
 * Inner component that uses @vis.gl/react-google-maps `useMapsLibrary('places')`
 * when rendered inside an `<APIProvider>`, or falls back cleanly if Places API is unavailable.
 */
export const PlacesAutocompleteInputInner: React.FC<{
  onSelectLocation: (loc: CustomerSelectedLocation) => void;
  onClearLocation?: () => void;
  onTextChange?: (text: string) => void;
  currentLabel?: string;
  placeholder?: string;
}> = ({ onSelectLocation, onClearLocation, onTextChange, currentLabel, placeholder }) => {
  const placesLib = useMapsLibrary('places');
  const sessionTokenRef = useRef<any>(null);

  const [inputValue, setInputValue] = useState(currentLabel || '');
  const [suggestions, setSuggestions] = useState<PlaceSuggestionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [placesNotice, setPlacesNotice] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentLabel !== undefined) {
      setInputValue(currentLabel);
    }
  }, [currentLabel]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchFallbackSuggestions = useCallback(async (queryText: string) => {
    try {
      const res = await fetch(
        `/api/location/autocomplete?text=${encodeURIComponent(queryText)}&limit=6`
      );
      if (!res.ok) return [];
      const data = await res.json();
      if (!data.success || !Array.isArray(data.results)) return [];
      return data.results
        .filter((r: any) => isValidCoordinatePair(r.lat ?? r.latitude, r.lon ?? r.lng ?? r.longitude))
        .map((r: any, idx: number) => ({
          id: r.placeId || `fallback-${idx}`,
          mainText: r.city || r.lga || r.formattedAddress?.split(',')[0] || queryText,
          secondaryText: r.formattedAddress || `${r.lga || ''}, ${r.state || 'Nigeria'}`,
          fullText: r.formattedAddress || `${r.city || ''}, ${r.state || 'Nigeria'}`,
          fallbackCoords: {
            lat: Number(r.lat ?? r.latitude),
            lng: Number(r.lon ?? r.lng ?? r.longitude),
            state: r.state,
            lga: r.lga,
          },
        }));
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < 2 || trimmed === currentLabel) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      setPlacesNotice(null);

      // 1. Try modern Google Places API (New): AutocompleteSuggestion.fetchAutocompleteSuggestions
      const AutocompleteSuggestion = (placesLib as any)?.AutocompleteSuggestion;
      const AutocompleteSessionToken = (placesLib as any)?.AutocompleteSessionToken;

      if (AutocompleteSuggestion?.fetchAutocompleteSuggestions && AutocompleteSessionToken) {
        try {
          if (!sessionTokenRef.current) {
            sessionTokenRef.current = new AutocompleteSessionToken();
          }

          const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: trimmed,
            includedRegionCodes: ['ng'],
            sessionToken: sessionTokenRef.current,
          });

          const rawList = response?.suggestions || [];
          if (rawList.length > 0) {
            const mapped: PlaceSuggestionItem[] = rawList
              .filter((s: any) => s.placePrediction)
              .slice(0, 6)
              .map((s: any, idx: number) => {
                const pred = s.placePrediction;
                const full = pred.text?.text || trimmed;
                const main = pred.mainText?.text || full.split(',')[0] || trimmed;
                const secondary = pred.secondaryText?.text || full;
                return {
                  id: pred.placeId || `gmp-${idx}`,
                  mainText: main,
                  secondaryText: secondary,
                  fullText: full,
                  rawPlacePrediction: pred,
                };
              });

            setSuggestions(mapped);
            setIsOpen(mapped.length > 0);
            setIsLoading(false);
            return;
          }
        } catch (err: any) {
          console.warn('[GooglePlacesAutocomplete] Falling back to Nigerian location index:', err?.message || err);
        }
      }

      // 2. Graceful fallback to server-side Nigerian address & LGA autocomplete
      const fallbackItems = await fetchFallbackSuggestions(trimmed);
      setSuggestions(fallbackItems);
      setIsOpen(fallbackItems.length > 0);
      setIsLoading(false);
    }, 260);

    return () => clearTimeout(timer);
  }, [inputValue, currentLabel, placesLib, fetchFallbackSuggestions]);

  const handleSelectSuggestion = async (item: PlaceSuggestionItem) => {
    setIsOpen(false);
    setIsLoading(true);
    setPlacesNotice(null);

    try {
      if (item.rawPlacePrediction && typeof item.rawPlacePrediction.toPlace === 'function') {
        const place = item.rawPlacePrediction.toPlace();
        await place.fetchFields({
          fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
        });

        // Reset session token after Place.fetchFields per Google Maps billing best practice
        sessionTokenRef.current = null;

        const loc = place.location;
        const lat = typeof loc?.lat === 'function' ? loc.lat() : loc?.lat;
        const lng = typeof loc?.lng === 'function' ? loc.lng() : loc?.lng;

        if (isValidCoordinatePair(lat, lng)) {
          let detectedState: string | undefined;
          let detectedLga: string | undefined;
          if (Array.isArray(place.addressComponents)) {
            for (const comp of place.addressComponents) {
              const types: string[] = comp.types || [];
              if (types.includes('administrative_area_level_1')) {
                detectedState = comp.longText || comp.shortText;
              }
              if (types.includes('administrative_area_level_2') || types.includes('locality')) {
                detectedLga = comp.longText || comp.shortText;
              }
            }
          }

          const label = place.formattedAddress || place.displayName || item.fullText;
          setInputValue(label);
          onSelectLocation({
            lat: Number(Number(lat).toFixed(5)),
            lng: Number(Number(lng).toFixed(5)),
            label,
            source: 'google_places',
            state: detectedState,
            lga: detectedLga,
          });
          setIsLoading(false);
          return;
        }
      }

      if (item.fallbackCoords && isValidCoordinatePair(item.fallbackCoords.lat, item.fallbackCoords.lng)) {
        setInputValue(item.fullText);
        onSelectLocation({
          lat: Number(item.fallbackCoords.lat.toFixed(5)),
          lng: Number(item.fallbackCoords.lng.toFixed(5)),
          label: item.fullText,
          source: 'google_places',
          state: item.fallbackCoords.state,
          lga: item.fallbackCoords.lga,
        });
      }
    } catch (err: any) {
      setPlacesNotice('Could not resolve coordinates for this address. Try adjusting the map pin.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      <div className="relative">
        <MapPin className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            const nextVal = e.target.value;
            setInputValue(nextVal);
            if (onTextChange) onTextChange(nextVal);
            if (!nextVal.trim() && onClearLocation) {
              onClearLocation();
            }
            setIsOpen(true);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder || 'Location (city, LGA, or street address)...'}
          aria-label="Location input with Google Maps autocomplete"
          className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white text-sm text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-colors"
        />
        {isLoading ? (
          <Loader2 className="w-4 h-4 text-emerald-600 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
        ) : inputValue ? (
          <button
            type="button"
            onClick={() => {
              setInputValue('');
              setSuggestions([]);
              setIsOpen(false);
              if (onTextChange) onTextChange('');
              if (onClearLocation) onClearLocation();
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 cursor-pointer"
            title="Clear location"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>

      {placesNotice && (
        <p className="text-[11px] text-amber-700 mt-1">{placesNotice}</p>
      )}

      {isOpen && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-1.5 bg-white rounded-2xl border border-slate-200 shadow-xl max-h-64 overflow-y-auto divide-y divide-slate-100"
        >
          {suggestions.map((item) => (
            <li
              key={item.id}
              role="option"
              onClick={() => handleSelectSuggestion(item)}
              className="px-4 py-2.5 hover:bg-emerald-50/70 cursor-pointer transition-colors flex items-start gap-2.5 text-left"
            >
              <MapPin className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-slate-900 truncate">{item.mainText}</div>
                <div className="text-[11px] text-slate-500 truncate">{item.secondaryText}</div>
              </div>
            </li>
          ))}
          <li className="px-4 py-1.5 bg-slate-50 text-[10px] text-slate-400 flex items-center justify-between">
            <span>Address search powered by Google Maps</span>
            <span>Nigeria (NG)</span>
          </li>
        </ul>
      )}
    </div>
  );
};

export const CustomerNearbyLocationBar: React.FC<CustomerNearbyLocationBarProps> = ({
  customerLocation,
  onLocationChange,
  radiusKm,
  onRadiusChange,
  onOpenMapPinAdjust,
  isMapVisible,
}) => {
  const [isLocatingGps, setIsLocatingGps] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [gpsWarning, setGpsWarning] = useState<string | null>(null);

  /**
   * Uses browser Geolocation API with explicit user permission.
   * Never silently substitutes IP-based geolocation for precise GPS.
   */
  const handleUseCurrentLocation = () => {
    setGpsError(null);
    setGpsWarning(null);

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGpsError(
        'Browser geolocation is not supported on this device. Please enter your address above or adjust the map pin.'
      );
      return;
    }

    setIsLocatingGps(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        if (!isValidCoordinatePair(latitude, longitude)) {
          setIsLocatingGps(false);
          setGpsError('Received invalid GPS coordinates from device. Please enter your address manually.');
          return;
        }

        if (!isWithinNigeriaBounds(latitude, longitude)) {
          setGpsWarning(
            'Your GPS location appears to be outside Nigeria. Showing distance from your GPS coordinates, or type a Nigerian address above.'
          );
        } else if (accuracy && accuracy > 2000) {
          setGpsWarning(
            `GPS accuracy is approximate (±${Math.round(accuracy / 100) / 10} km). Drag your location pin on the map or enter your street address for exact matching.`
          );
        }

        // Reverse geocode label for user clarity without overriding GPS coordinates
        let resolvedLabel = `Current GPS Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
        let detectedState: string | undefined;
        let detectedLga: string | undefined;

        try {
          const revRes = await fetch(
            `/api/location/reverse?lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`
          );
          if (revRes.ok) {
            const revData = await revRes.json();
            if (revData?.result?.formattedAddress) {
              resolvedLabel = revData.result.formattedAddress;
              detectedState = revData.result.state;
              detectedLga = revData.result.lga;
            }
          }
        } catch {
          // Keep coordinate label if reverse lookup fails
        }

        onLocationChange({
          lat: Number(latitude.toFixed(5)),
          lng: Number(longitude.toFixed(5)),
          label: resolvedLabel,
          source: 'browser_gps',
          accuracyMeters: accuracy ? Math.round(accuracy) : undefined,
          state: detectedState,
          lga: detectedLga,
        });
        setIsLocatingGps(false);
      },
      (err) => {
        setIsLocatingGps(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGpsError(
            'Location permission was denied. Please allow location access in your browser settings, or search your address manually above.'
          );
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGpsError(
            'Precise GPS signal is unavailable right now. We do not substitute approximate IP location—please type your address or drop a pin on the map.'
          );
        } else if (err.code === err.TIMEOUT) {
          setGpsError(
            'GPS location request timed out. Please try again outdoors or enter your address manually.'
          );
        } else {
          setGpsError(
            'Could not determine your GPS location. Please enter your address or adjust the map pin.'
          );
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      }
    );
  };

  return (
    <div className="bg-emerald-950/5 rounded-2xl p-3.5 sm:p-4 border border-emerald-900/10 space-y-3">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        {/* Left: Title & Active Location Status */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-xs">
            <MapPin className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-extrabold text-slate-900">
                Nearby Professionals & Radius Search
              </span>
              {customerLocation ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-900 text-[10px] font-bold">
                  <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                  {customerLocation.source === 'browser_gps'
                    ? 'GPS Verified'
                    : customerLocation.source === 'map_pin'
                    ? 'Pin Adjusted'
                    : 'Address Selected'}
                </span>
              ) : (
                <span className="text-[11px] text-slate-500">
                  Set your location to filter by 5–50 km radius & sort by closest
                </span>
              )}
            </div>
            {customerLocation && (
              <p className="text-[11px] text-slate-600 truncate max-w-md mt-0.5">
                Searching within <strong>{radiusKm} km</strong> of{' '}
                <strong className="text-slate-900">{customerLocation.label}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Right: Radius Choices (5 km, 10 km, 20 km, 50 km) */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-bold text-slate-600 flex items-center gap-1 mr-1">
            <SlidersHorizontal className="w-3 h-3 text-emerald-700" />
            <span>Radius:</span>
          </span>
          {ALLOWED_NEARBY_RADII_KM.map((r) => {
            const active = radiusKm === r && Boolean(customerLocation);
            return (
              <button
                key={r}
                type="button"
                onClick={() => onRadiusChange(r)}
                className={`px-2.5 py-1 rounded-lg text-xs font-extrabold transition-all cursor-pointer border ${
                  active
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-2xs'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                }`}
              >
                {r} km
              </button>
            );
          })}
          {customerLocation && (
            <button
              type="button"
              onClick={() => {
                onLocationChange(null);
                setGpsError(null);
                setGpsWarning(null);
              }}
              className="ml-1 px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 border border-transparent cursor-pointer"
              title="Clear location radius filter"
            >
              Clear Pin
            </button>
          )}
        </div>
      </div>

      {/* Location Input Row: Places Autocomplete + Use My Current Location + Adjust Map Pin */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <PlacesAutocompleteInputInner
          currentLabel={customerLocation?.label || ''}
          onSelectLocation={(loc) => {
            setGpsError(null);
            setGpsWarning(null);
            onLocationChange(loc);
          }}
        />

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            id="use-my-current-location-btn"
            onClick={handleUseCurrentLocation}
            disabled={isLocatingGps}
            className="flex-1 sm:flex-initial px-3.5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-bold shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
          >
            {isLocatingGps ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Locating GPS...</span>
              </>
            ) : (
              <>
                <Navigation className="w-3.5 h-3.5" />
                <span>Use my current location</span>
              </>
            )}
          </button>

          {onOpenMapPinAdjust && (
            <button
              type="button"
              onClick={onOpenMapPinAdjust}
              className="px-3 py-2.5 rounded-xl bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              title="Adjust your location pin directly on the map"
            >
              <Move className="w-3.5 h-3.5 text-emerald-600" />
              <span className="hidden md:inline">
                {isMapVisible ? 'Drag Pin on Map' : 'Adjust Pin on Map'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Graceful Error & Accuracy Warnings */}
      {gpsError && (
        <div
          role="alert"
          className="p-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2"
        >
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <span className="leading-relaxed">{gpsError}</span>
        </div>
      )}

      {gpsWarning && (
        <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <span className="leading-relaxed">{gpsWarning}</span>
        </div>
      )}
    </div>
  );
};
